import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Tab,
	Tabs,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { QuestionSlim, useSearchQuestions } from '@/app2/shared/data';
import { useAccessToken } from '@/app2/shared/hooks/useAccessToken';
import { toolbarHeight, palette } from '../../../../../constants';
import AddQuizNewQuestionForm from './AddQuizNewQuestionForm';

export interface AddQuestionsModalProps {
	open: boolean;
	currentQuestions?: QuestionSlim[];
	onClose: (newQuestions?: QuestionSlim[]) => void;
}

const DIALOG_TOP = `calc(${toolbarHeight} + env(safe-area-inset-top, 0px))`;
const DIALOG_HEIGHT = `calc(100vh - ${toolbarHeight} - env(safe-area-inset-top, 0px))`;

const useStyles = makeStyles()((theme) => ({
	newQuestionModal: {
		zIndex: theme.zIndex.modal,
	},
	dialogPaper: {
		margin: 0,
		maxWidth: '100%',
		width: '100%',
		position: 'fixed',
		top: DIALOG_TOP,
		left: 0,
		right: 0,
		height: DIALOG_HEIGHT,
		maxHeight: DIALOG_HEIGHT,
		borderRadius: 0,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: theme.palette.background.default,
	},
	selectedCountFooter: {
		color: 'grey',
		paddingRight: '15px',
		fontSize: '12px',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		paddingTop: theme.spacing(2),
	},
	title: {
		flexGrow: 2,
	},
	tabs: {
		height: '70px',
		minHeight: '70px',
		borderBottom: theme.palette.divider + ' 1px solid',
		width: '100%',
		'& button': {
			width: '50%',
		},
	},
	searchContainer: {
		padding: '20px',
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: '10px',
	},
	searchHeader: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(2.5),
		marginBottom: theme.spacing(2.5),
		alignItems: 'flex-start',
	},
	searchTypeControl: {
		minWidth: 200,
		maxWidth: 240,
		flexShrink: 0,
	},
	searchTypeInput: {
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: `${palette.grey15} !important`,
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: `${palette.grey40} !important`,
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderWidth: 1,
			borderColor: `${palette.orange} !important`,
		},
		'& .MuiSelect-select': {
			display: 'flex',
			alignItems: 'center',
			minHeight: 36,
			paddingTop: theme.spacing(1),
			paddingBottom: theme.spacing(1),
		},
	},
	searchTextField: {
		flex: '1 1 240px',
		minWidth: 200,
	},
	tableRow: {
		'&:hover': {
			cursor: 'pointer',
			backgroundColor: theme.palette.action.hover,
		},
	},
}));

const QUESTION_TYPE_OPTIONS = [
	{ value: '', label: 'All Types' },
	{ value: 'boolean_choice_questions', label: 'True/False' },
	{ value: 'single_choice_questions', label: 'Single Choice' },
	{ value: 'multiple_choice_questions', label: 'Multiple Choice' },
];

const AddQuestionsModal: React.FC<AddQuestionsModalProps> = ({
	open,
	currentQuestions = [],
	onClose,
}) => {
	const { classes } = useStyles();
	const [tab, setTab] = useState<'search' | 'new'>('new');
	const [savedNewQuestion, setSavedNewQuestion] = useState<QuestionSlim | null>(null);
	const [selected, setSelected] = useState<QuestionSlim[]>([]);
	const [questionType, setQuestionType] = useState<string>('');
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [rawSearchResults, setRawSearchResults] = useState<QuestionSlim[]>([]);
	const [filterMap, setFilterMap] = useState<Record<string, Record<number, boolean>>>({});

	const {
		mutate: runSearch,
		isPending: searchPending,
		isError: searchFailed,
		error: searchError,
		reset: resetSearchMutation,
	} = useSearchQuestions();
	const accessToken = useAccessToken();

	useEffect(() => {
		if (open) {
			setSavedNewQuestion(null);
			resetSearchMutation();
		}
	}, [open, resetSearchMutation]);

	const handleDraftEdited = useCallback(() => {
		setSavedNewQuestion((prev) => (prev == null ? prev : null));
	}, []);

	useEffect(() => {
		const map: Record<string, Record<number, boolean>> = {};
		currentQuestions.forEach((q) => {
			if (!map[q.__type]) {
				map[q.__type] = {};
			}
			map[q.__type][q.id] = true;
		});
		setFilterMap(map);
	}, [currentQuestions]);

	const questions = useMemo(() => {
		const trimmed = searchTerm.trim().toLowerCase();
		return rawSearchResults.filter((q) => {
			if (filterMap[q.__type]?.[q.id]) return false;
			if (questionType && q.__type !== questionType) return false;
			if (trimmed && !(q.title ?? '').toLowerCase().includes(trimmed)) return false;
			return true;
		});
	}, [rawSearchResults, filterMap, questionType, searchTerm]);

	// Load once per Import visit; filter rows locally via searchTerm / questionType.
	useEffect(() => {
		if (!open || tab !== 'search' || !accessToken) return;
		let cancelled = false;
		runSearch(
			{},
			{
				onSuccess: (data) => {
					if (cancelled) return;
					const allQuestions: QuestionSlim[] = [];
					Object.values(data).forEach((typeQuestions) => {
						if (Array.isArray(typeQuestions)) {
							allQuestions.push(...typeQuestions);
						}
					});
					setRawSearchResults(allQuestions);
				},
			}
		);
		return () => {
			cancelled = true;
		};
	}, [open, tab, accessToken, runSearch]);

	const handleToggleQuestion = (question: QuestionSlim) => {
		setSelected((prev) => {
			const isSelected = prev.find((q) => q.__type === question.__type && q.id === question.id);
			if (isSelected) {
				return prev.filter((q) => !(q.__type === question.__type && q.id === question.id));
			}
			return [...prev, question];
		});
	};

	const handleToggleAll = (checked: boolean) => {
		if (checked) {
			setSelected(questions);
		} else {
			setSelected([]);
		}
	};

	const formatQuestionType = (type: string): string => {
		const known = QUESTION_TYPE_OPTIONS.find((o) => o.value === type);
		if (known) return known.label;
		return type.replace(/_/g, ' ').replace('questions', '').replace('question', '').trim();
	};

	const handleTabChange = (_e: React.SyntheticEvent, value: 'search' | 'new'): void => {
		setTab(value);
		if (value === 'search') {
			setSavedNewQuestion(null);
		}
	};

	const handleFooterAdd = (): void => {
		if (tab === 'search') {
			onClose(selected.length > 0 ? selected : undefined);
			return;
		}
		if (savedNewQuestion) {
			onClose([savedNewQuestion]);
		}
	};

	const addButtonDisabled =
		tab === 'search' ? selected.length === 0 : savedNewQuestion == null;

	const addButtonSx = {
		textTransform: 'uppercase' as const,
		fontWeight: 600,
		fontSize: '0.875rem',
		borderRadius: '6px',
		borderColor: `${palette.orange} !important`,
		color: `${palette.white} !important`,
		'&:hover': {
			borderColor: `${palette.orange} !important`,
			bgcolor: palette.orange08,
		},
		'&.Mui-disabled': {
			borderColor: 'rgba(255, 158, 0, 0.35) !important',
			color: 'rgba(255, 255, 255, 0.35) !important',
		},
	};

	return (
		<Dialog
			open={open}
			onClose={() => onClose()}
			className={classes.newQuestionModal}
			maxWidth={false}
			fullWidth
			PaperProps={{ className: classes.dialogPaper }}
			BackdropProps={{
				sx: {
					top: DIALOG_TOP,
					height: DIALOG_HEIGHT,
					bottom: 'auto',
				},
			}}
		>
			<DialogTitle className={classes.header} component="div">
				<Typography className={classes.title} variant="h3" component="span">
					Add Questions
				</Typography>
			</DialogTitle>
			<DialogContent
				dividers
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
					backgroundColor: 'background.default',
				}}
			>
				<Box>
					<Tabs className={classes.tabs} value={tab} onChange={handleTabChange}>
						<Tab label="Create New" value="new" />
						<Tab label="Import" value="search" />
					</Tabs>

					{tab === 'new' && (
						<Box padding={3}>
							<AddQuizNewQuestionForm onSaved={setSavedNewQuestion} onDraftEdited={handleDraftEdited} />
						</Box>
					)}

					{tab === 'search' && (
						<Box className={classes.searchContainer}>
							<Box className={classes.searchHeader}>
								<FormControl className={classes.searchTypeControl} variant="outlined" size="small">
									<InputLabel id="add-questions-search-qtype-label" shrink>
										Question Type
									</InputLabel>
									<Select
										className={classes.searchTypeInput}
										labelId="add-questions-search-qtype-label"
										value={questionType}
										onChange={(e) => setQuestionType(e.target.value)}
										label="Question Type"
										displayEmpty
										notched
									>
										{QUESTION_TYPE_OPTIONS.map((option) => (
											<MenuItem key={option.value} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<TextField
									className={classes.searchTextField}
									placeholder="Search questions..."
									label="Search"
									name="question-search"
									onChange={(e) => setSearchTerm(e.target.value)}
									value={searchTerm}
									variant="outlined"
									size="small"
									InputLabelProps={{ shrink: true }}
								/>
							</Box>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>
											<Checkbox
												checked={questions.length > 0 && selected.length === questions.length}
												onChange={(e) => handleToggleAll(e.target.checked)}
												aria-label="Select all entries"
											/>
										</TableCell>
										<TableCell>
											<Typography>Type</Typography>
										</TableCell>
										<TableCell>
											<Typography>Title</Typography>
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{!accessToken || searchPending ? (
										<TableRow>
											<TableCell colSpan={3} align="center">
												<Typography>Loading...</Typography>
											</TableCell>
										</TableRow>
									) : searchFailed ? (
										<TableRow>
											<TableCell colSpan={3} align="center">
												<Typography color="error">
													{searchError?.message ?? 'Search failed. Try again.'}
												</Typography>
											</TableCell>
										</TableRow>
									) : questions.length === 0 ? (
										<TableRow>
											<TableCell colSpan={3} align="center">
												<Typography color="textSecondary">
													{searchTerm || questionType ? 'No questions found' : 'No questions available'}
												</Typography>
											</TableCell>
										</TableRow>
									) : (
										questions.map((question) => {
											const isSelected = !!selected.find(
												(q) => q.__type === question.__type && q.id === question.id
											);
											return (
												<TableRow
													key={`${question.__type}-${question.id}`}
													onClick={() => handleToggleQuestion(question)}
													className={classes.tableRow}
												>
													<TableCell>
														<Checkbox checked={isSelected} aria-label={`Select ${question.title}`} />
													</TableCell>
													<TableCell>{formatQuestionType(question.__type)}</TableCell>
													<TableCell>{question.title}</TableCell>
												</TableRow>
											);
										})
									)}
								</TableBody>
							</Table>
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions
				sx={{
					justifyContent: 'space-between',
					flexWrap: 'wrap',
					gap: 1,
					px: 3,
					py: 2,
					backgroundColor: 'background.default',
					borderTop: (theme) => `1px solid ${theme.palette.divider}`,
				}}
			>
				<Button
					variant="outlined"
					onClick={() => onClose()}
					sx={{
						textTransform: 'uppercase',
						fontWeight: 600,
						fontSize: '0.875rem',
						borderRadius: '6px',
						borderColor: `${palette.orange} !important`,
						color: `${palette.white} !important`,
						'&:hover': { borderColor: `${palette.orange} !important`, bgcolor: palette.orange08 },
					}}
				>
					Cancel
				</Button>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
					{tab === 'search' && selected.length > 0 && (
						<Typography className={classes.selectedCountFooter}>
							Selected {selected.length} questions
						</Typography>
					)}
					<Button
						disabled={addButtonDisabled}
						variant="outlined"
						onClick={handleFooterAdd}
						sx={addButtonSx}
					>
						Add
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default AddQuestionsModal;
